'use client';

import {
  Badge,
  Button,
  Card,
  Checkbox,
  cn,
  Input,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@routly/ui';
import { Save } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ErrorState, LoadingState, PageHeader } from '@/components/shared';
import { useCalculators, useSaveCalculators } from '@/hooks/useCalculators';
import type { Calculator, CalculatorsProfile } from '@/lib/types';
import { BackfillPanel } from './BackfillPanel';
import { BehaviorFields } from './BehaviorFields';
import { CounterFields } from './CounterFields';
import { SelectorFields } from './SelectorFields';

/**
 * What the interval engine runs, and editing it.
 *
 * Shows the effective bundle — the shipped defaults with this organisation's
 * overrides already applied — because that is what the engine will actually
 * use. The stored override on its own is a diff against something invisible,
 * and reading a diff is not how anybody checks whether a rule is right.
 *
 * Saving writes the whole bundle at the organisation rung. That is heavier than
 * writing the one calculator that changed, and it is what makes the JSON tab
 * honest: what is on screen is what will be stored.
 */
export function CalculatorsPage() {
  const { data, isLoading, error } = useCalculators();
  const save = useSaveCalculators();

  const [draft, setDraft] = useState<CalculatorsProfile | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [json, setJson] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const bundle = useMemo(() => draft ?? data?.effective ?? {}, [draft, data]);
  const slugs = Object.keys(bundle).sort();
  const active = selected ?? slugs[0] ?? null;
  const calculator = active ? bundle[active] : null;

  if (isLoading) return <LoadingState />;
  if (error || !data) return <ErrorState title="Could not load calculators" />;

  const update = (slug: string, next: Calculator) => setDraft({ ...bundle, [slug]: next });

  const handleSave = async (config: CalculatorsProfile) => {
    try {
      await save.mutateAsync(config);
      setDraft(null);
      setJson(null);
      toast.success('Calculators saved', { description: 'The next message will use them.' });
    } catch (saveError) {
      // The API validates the whole document with the same Zod schema the
      // engine parses it with, so a rejection here is the rule being wrong
      // rather than the form being wrong — worth showing verbatim.
      toast.error('Rejected', {
        description: saveError instanceof Error ? saveError.message : 'Something went wrong',
      });
    }
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(json ?? '') as CalculatorsProfile;
      setJsonError(null);
      setDraft(parsed);
      return parsed;
    } catch {
      setJsonError('That is not valid JSON');
      return null;
    }
  };

  return (
    <>
      <PageHeader
        title="Calculators"
        description="What becomes an interval: the conditions, how long they must hold, and what the interval remembers."
      />

      <Tabs defaultValue="visual">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="visual">Composer</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
          </TabsList>

          <Button onClick={() => handleSave(bundle)} disabled={draft === null || save.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
        </div>

        <TabsContent value="visual" className="mt-4">
          <div className="flex gap-4">
            <Card className="w-56 shrink-0 p-2">
              {slugs.map((slug) => (
                <button
                  key={slug}
                  type="button"
                  onClick={() => setSelected(slug)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                    slug === active ? 'bg-secondary text-foreground' : 'text-muted-foreground'
                  )}
                >
                  <span>{bundle[slug].name || slug}</span>
                  {bundle[slug].isEnabled ? null : <Badge variant="outline">off</Badge>}
                </button>
              ))}
            </Card>

            {calculator && active ? (
              <Card className="flex-1 space-y-6 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={calculator.name}
                      onChange={(event) =>
                        update(active, { ...calculator, name: event.target.value })
                      }
                    />
                  </div>
                  <label className="flex items-center gap-2 self-end pb-2 text-sm">
                    <Checkbox
                      checked={calculator.isEnabled}
                      onChange={(event) =>
                        update(active, { ...calculator, isEnabled: event.target.checked })
                      }
                    />
                    Enabled
                  </label>
                </div>

                <section className="space-y-2">
                  <h3 className="text-sm font-medium text-foreground">Active when</h3>
                  <SelectorFields
                    selectors={calculator.selectors}
                    onChange={(selectors) => update(active, { ...calculator, selectors })}
                  />
                </section>

                <section className="space-y-2">
                  <h3 className="text-sm font-medium text-foreground">Timing</h3>
                  <BehaviorFields
                    behavior={calculator.behavior}
                    onChange={(behavior) => update(active, { ...calculator, behavior })}
                  />
                </section>

                <section className="space-y-2">
                  <h3 className="text-sm font-medium text-foreground">Remembers</h3>
                  <CounterFields
                    counters={calculator.counters}
                    onChange={(counters) => update(active, { ...calculator, counters })}
                  />
                </section>
              </Card>
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="json" className="mt-4 space-y-3">
          <textarea
            aria-label="Calculator bundle"
            className="h-[32rem] w-full rounded-md border border-border bg-background p-3 font-mono text-xs text-foreground"
            value={json ?? JSON.stringify(bundle, null, 2)}
            onChange={(event) => setJson(event.target.value)}
          />
          {jsonError ? <p className="text-sm text-destructive">{jsonError}</p> : null}
          <Button variant="outline" onClick={applyJson} disabled={json === null}>
            Apply to composer
          </Button>
        </TabsContent>
      </Tabs>

      {/* Below the composer on purpose: changing a rule and making yesterday
          agree with it are two steps, and the second one is the answer to
          "why does the week I am looking at still show the old thresholds". */}
      <BackfillPanel />
    </>
  );
}
